import { NamespaceRoutes, resolveRoute } from '../routes';
import { ApiMethod, PromiseApi, RequestConfig, RequestPayload } from '../types';
// Temporary compatibility boundary; typed generated clients will replace this during the endpoint migration.
import { decodeError, decodeResponse, encodeRequest, loadCodecs } from '../generated/wire-contract';

type Props<PayloadT extends RequestPayload, ConfigT extends RequestConfig = never> = {
  base: string;
  namespace: string;
  routes?: NamespaceRoutes;
  method: string;
  payload: PayloadT;
  config?: ConfigT;
};

const queryString = (payload: RequestPayload) => {
  const params = new URLSearchParams();
  if (!Array.isArray(payload)) {
    Object.entries(payload as Record<string, unknown>).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) value.forEach(item => params.append(key, String(item)));
      else if (typeof value === 'object') params.set(key, JSON.stringify(value));
      else if (typeof value === 'string') params.set(key, value);
      else if (typeof value === 'number' || typeof value === 'boolean') params.set(key, value.toString());
    });
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

// No real response body available (e.g. a network-level failure, or a response
// that isn't valid JSON). Deliberately has no `message` field - consumers are
// expected to supply their own fallback text rather than treat this as a real
// server message.
const NoResponseData = {};

const contractBase = (base: string) => {
  const path = new URL(base, 'http://batch.local').pathname.replace(/\/$/, '');
  for (const suffix of ['/auth/session', '/auth/sso', '/auth/v1', '/billing/v1', '/sign/v1', '/payroll/v1', '/sign', '/payroll', '/auditlog']) {
    if (path.endsWith(suffix)) return suffix;
  }
  return path.endsWith('/v1') ? '/v1' : '';
};

export default async function fetchTransport<T extends ApiMethod, ConfigT extends RequestConfig = never>({
  base = '',
  namespace,
  routes,
  method,
  payload,
  config,
}: Props<T['payload'], ConfigT>): PromiseApi<T> {
  let route;
  try {
    route = resolveRoute(routes, method, payload);
  } catch (error) {
    return Promise.reject(error);
  }
  if (!route) {
    return Promise.reject(new Error(`No route declared for ${namespace}:${method}`));
  }
  const headers = parseConfig(config || {});
  const generatedKey = globalThis.crypto?.randomUUID?.();
  if (route.httpMethod === 'POST' && !headers['Idempotency-Key'] && generatedKey) headers['Idempotency-Key'] = generatedKey;
  const relativeOperation = route.operation || `${route.httpMethod} ${route.path}`;
  const separator = relativeOperation.indexOf(' ');
  const operation = `${relativeOperation.slice(0, separator)} ${contractBase(base)}${relativeOperation.slice(separator + 1)}`;
  const codecs = await loadCodecs(operation);
  const wirePayload = encodeRequest(codecs, operation, payload) as RequestPayload;
  const url = `${base}${route.path}${route.httpMethod === 'GET' ? queryString(wirePayload) : ''}`;
  return fetch(url, {
    headers,
    body: route.httpMethod === 'GET' ? undefined : JSON.stringify(wirePayload),
    method: route.httpMethod,
    mode: 'cors',
    credentials: 'include',
  }).then(
    async response => {
      const respParsed = { status: response.status };
      if (response.status >= 200 && response.status < 300) {
        // empty content status
        if (response.status === 204) {
          // ResponseApi's type claims the full Response shape (json(), headers, ok, ...), but spreading a
          // Response instance only copies its own enumerable properties — status/etc. are prototype getters,
          // so this never actually carried them at runtime either. Fixing that honestly means reworking
          // ResponseApi's type across the whole API layer, which is out of scope here.
          // oxlint-disable-next-line no-misused-spread
          return Promise.resolve({ ...response, data: null });
        }
        try {
          const data = decodeResponse(codecs, operation, await response.json()) as T['success'];
          // oxlint-disable-next-line no-misused-spread
          return Promise.resolve({ ...response, data });
        } catch (err) {
          return Promise.reject({ ...respParsed, data: NoResponseData });
        }
      } else {
        try {
          const data = decodeError(await response.json());
          return Promise.reject({ ...respParsed, data });
        } catch (err) {
          return Promise.reject({ ...respParsed, data: NoResponseData });
        }
      }
    },
    async response => {
      return Promise.reject({
        ...response,
        data: NoResponseData,
      });
    }
  );
}

function parseConfig(config: RequestConfig) {
  const res: Record<string, string> = {};
  const headers = Object.keys(config).reduce((result, key) => {
    const value = config[key];
    if (!value) {
      return result;
    }
    result[key] = typeof value === 'number' ? value.toString() : value;
    return result;
  }, res);
  return headers;
}
